import { Switch, Text, TextInput } from "@mantine/core";
import { type CellContext, createColumnHelper } from "@tanstack/react-table";

import { ActionButton } from "./action-button";
import { InviteButton } from "./invite-button";
import { RoleSelect } from "./role-select";
import { StatusBadge } from "./status-badge";
import {
  type DraftUser,
  type UserManagementListUser,
  type UserManagementTableMeta,
  UserRole,
  UserStatus,
} from "@/types/user";
import { isValidEmail } from "@/utils/helpers";
import { anyEmptyAfterTrim } from "@/utils/string";

export type ExistingUserRow = UserManagementListUser & { isDraft: false };
export type UserManagementTableRow = DraftUser | ExistingUserRow;

export type ValidDraftUser = DraftUser & { role: UserRole };

type DraftUserValidationResult =
  | { isValid: true; draft: ValidDraftUser }
  | { isValid: false; reason: string };

export const validateDraftUser = (
  draft: DraftUser,
): DraftUserValidationResult => {
  if (anyEmptyAfterTrim(draft.name, draft.email) || !draft.role) {
    return { isValid: false, reason: "All fields are required" };
  }
  if (!isValidEmail(draft.email)) {
    return { isValid: false, reason: "Enter a valid email address" };
  }
  return { isValid: true, draft: draft as ValidDraftUser };
};

const getMeta = (
  info: CellContext<UserManagementTableRow, unknown>,
): UserManagementTableMeta => {
  const { meta } = info.table.options;

  if (!meta?.userManagement) {
    throw new TypeError(
      "Attempted to resolve User Management Table meta but it was not defined.",
    );
  }

  return meta.userManagement;
};

const columnHelper = createColumnHelper<UserManagementTableRow>();
const headerProps = {
  c: "gray.9",
  className: "tracking-wider uppercase",
  fw: 600,
  size: "xs",
};

const getRowUserAndMeta = (
  info: CellContext<UserManagementTableRow, unknown>,
) => [info.row.original, getMeta(info)] as const;

const emailIsDuplicate = (
  info: CellContext<UserManagementTableRow, unknown>,
) => {
  const currentEmail = info.row.original.email.trim().toLowerCase();
  if (!currentEmail) {
    return false;
  }

  return new Set(
    info.table
      .getRowModel()
      .rows.filter((row) => row.id !== info.row.id)
      .map(({ original }) => original.email.trim().toLowerCase()),
  ).has(currentEmail);
};

export const columns = [
  columnHelper.display({
    id: "name",
    cell: (info) => {
      const [rowUser, meta] = getRowUserAndMeta(info);

      if (rowUser.isDraft) {
        return (
          <TextInput
            classNames={{
              input: "border-uw-gray-2 h-10 rounded text-sm",
            }}
            onBlur={(e) => {
              const trimmed = e.currentTarget.value.trim();
              if (trimmed !== e.currentTarget.value) {
                meta.onUpdateDraft(rowUser.tempId, { name: trimmed });
              }
            }}
            onChange={(e) =>
              meta.onUpdateDraft(rowUser.tempId, {
                name: e.currentTarget.value,
              })
            }
            placeholder="Name"
            value={rowUser.name}
          />
        );
      }
      return (
        <Text fw={600} size="sm" c="gray.9">
          {rowUser.fullName ?? "—"}
        </Text>
      );
    },
    header: () => <Text {...headerProps}>Name</Text>,
    size: 180,
  }),
  columnHelper.display({
    id: "email",
    cell: (info) => {
      const [rowUser, meta] = getRowUserAndMeta(info);

      if (rowUser.isDraft) {
        return (
          <TextInput
            classNames={{
              input: "border-uw-gray-2 h-10 rounded text-sm",
            }}
            error={emailIsDuplicate(info) ? "This user already exists" : null}
            onBlur={(e) => {
              const trimmed = e.currentTarget.value.trim();
              if (trimmed !== e.currentTarget.value) {
                meta.onUpdateDraft(rowUser.tempId, { email: trimmed });
              }
            }}
            onChange={(e) =>
              meta.onUpdateDraft(rowUser.tempId, {
                email: e.currentTarget.value,
              })
            }
            placeholder="Email"
            value={rowUser.email}
          />
        );
      }
      return (
        <Text size="sm" c="gray.9">
          {rowUser.email}
        </Text>
      );
    },
    header: () => <Text {...headerProps}>Email</Text>,
    size: 240,
  }),
  columnHelper.display({
    id: "role",
    cell: (info) => {
      const [rowUser, meta] = getRowUserAndMeta(info);

      if (rowUser.isDraft) {
        return (
          <RoleSelect
            role={rowUser.role}
            onChange={(role) => meta.onUpdateDraft(rowUser.tempId, { role })}
          />
        );
      }
      const isCurrentUser = rowUser.id === meta.currentUserId;
      return (
        <RoleSelect
          disabled={isCurrentUser}
          role={rowUser.role}
          onChange={(role) => meta.onRoleChange(rowUser.id, role)}
        />
      );
    },
    header: () => <Text {...headerProps}>Role</Text>,
    size: 170,
  }),
  columnHelper.display({
    id: "sendPdfs",
    cell: (info) => {
      const [rowUser, meta] = getRowUserAndMeta(info);

      if (rowUser.isDraft) {
        return <Switch disabled size="sm" />;
      }

      const isInactive = rowUser.status === UserStatus.Inactive;

      return (
        <Switch
          aria-label={`Allow ${rowUser.email} to send candidate PDFs`}
          checked={rowUser.canSendCandidatePdfs}
          disabled={isInactive}
          onChange={(e) =>
            meta.onSendPdfsChange(rowUser.id, e.currentTarget.checked)
          }
          size="sm"
        />
      );
    },
    header: () => <Text {...headerProps}>Send PDFs</Text>,
    size: 100,
  }),
  columnHelper.display({
    id: "status",
    cell: (info) => {
      const [rowUser, meta] = getRowUserAndMeta(info);

      if (rowUser.isDraft) {
        const validationResult = validateDraftUser(rowUser);
        const isDuplicateEmail = emailIsDuplicate(info);

        return (
          <InviteButton
            disabled={!validationResult.isValid || isDuplicateEmail}
            disabledReason={
              isDuplicateEmail
                ? "This user already exists"
                : validationResult.isValid
                  ? undefined
                  : validationResult.reason
            }
            loading={meta.isInvitePending(rowUser.tempId)}
            onClick={() => meta.onInvite(rowUser)}
          />
        );
      }
      return <StatusBadge status={rowUser.status} />;
    },
    header: () => <Text {...headerProps}>Status</Text>,
    size: 100,
  }),
  columnHelper.display({
    id: "actions",
    cell: (info) => {
      const [rowUser, meta] = getRowUserAndMeta(info);

      if (rowUser.isDraft) {
        return (
          <ActionButton
            isDraft
            onRemoveDraft={() => meta.onRemoveDraft(rowUser.tempId)}
          />
        );
      }

      const isCurrentUser = rowUser.id === meta.currentUserId;
      const isInactive = rowUser.status === UserStatus.Inactive;

      return (
        <ActionButton
          hidden={isCurrentUser}
          isInactive={isInactive}
          isReInviteLoading={meta.isReInvitePending(rowUser.id)}
          onDeactivate={() => meta.onDeactivate(rowUser.id)}
          onReInvite={() => meta.onReInvite(rowUser.id)}
        />
      );
    },
    size: 100,
  }),
];
